import { Sun, Moon, Compass, Activity, Heart, Sparkle, User } from "lucide-react";
import ShareCard from "@/components/earthpulse/ShareCard";

function Skeleton() {
  return (
    <div className="parchment-card rounded-2xl p-10 md:p-14">
      <div className="label-overline">Today&apos;s Briefing</div>
      <div className="mt-6 h-12 w-3/4 bg-[color:var(--bg-elevated)]/70 rounded animate-pulse" />
      <div className="mt-4 h-4 w-full bg-[color:var(--bg-elevated)]/50 rounded animate-pulse" />
      <div className="mt-2 h-4 w-5/6 bg-[color:var(--bg-elevated)]/50 rounded animate-pulse" />
    </div>
  );
}

function PatternStat({ label, value }) {
  return (
    <div className="rounded-xl border border-[color:var(--gold)]/15 p-4" style={{ background: "rgba(10, 17, 40, 0.4)" }}>
      <div className="label-overline">{label}</div>
      <div className="mt-1 font-serif-display text-[color:var(--parchment)] text-2xl tracking-tight">
        {value}
      </div>
    </div>
  );
}

export default function DailyBrief({ brief, isLoading }) {
  if (isLoading || !brief) return <Skeleton />;
  const b = brief.brief || {};
  const patterns = brief.personal_patterns;
  const sensitivity = b.sensitivity_level || "moderate";
  const sensColor =
    sensitivity === "high"
      ? "var(--accent-warning)"
      : sensitivity === "moderate"
      ? "var(--rose-gold)"
      : "var(--gold)";

  return (
    <article
      className="parchment-card rounded-2xl p-10 md:p-14 relative overflow-hidden"
      data-testid="daily-brief"
    >
      <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
        <div className="label-overline constellation-underline">Today&apos;s Briefing</div>
        <div className="flex items-center gap-4">
          <ShareCard brief={brief} />
          <div className="flex items-center gap-2">
            <span className="label-overline">Sensitivity</span>
            <span
              className="font-serif-italic text-sm px-3 py-1 rounded-full border capitalize"
              style={{ color: sensColor, borderColor: sensColor }}
              data-testid="brief-sensitivity"
            >
              {sensitivity}
            </span>
          </div>
        </div>
      </div>

      <h1
        className="font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-4xl"
        data-testid="brief-headline"
        style={{ fontVariationSettings: '"SOFT" 40, "WONK" 0' }}
      >
        {b.headline}
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10">
        <div>
          <div className="flex items-center gap-2 label-overline text-[color:var(--gold)]">
            <Sun size={12} /> Sky report
          </div>
          <p className="mt-3 text-[color:var(--parchment-soft)] leading-relaxed text-base md:text-lg font-sans-body">
            {b.sky_report}
          </p>
        </div>
        <div>
          <div className="flex items-center gap-2 label-overline text-[color:var(--rose-gold)]">
            <Moon size={12} /> Earth report
          </div>
          <p className="mt-3 text-[color:var(--parchment-soft)] leading-relaxed text-base md:text-lg font-sans-body">
            {b.earth_report}
          </p>
        </div>
      </div>

      {b.personal_reflection && (patterns?.entry_count > 0) && (
        <div
          className="mt-12 rounded-2xl border border-[color:var(--gold)]/30 p-7 md:p-8 stagger-in"
          style={{
            background: "linear-gradient(135deg, rgba(212, 162, 76, 0.10), rgba(229, 168, 124, 0.04))",
          }}
          data-testid="personal-reflection"
        >
          <div className="flex items-center gap-2 label-overline text-[color:var(--gold)]">
            <User size={12} /> A note about you
          </div>
          <p className="mt-4 font-serif-display text-[color:var(--parchment)] text-xl md:text-2xl leading-snug tracking-tight">
            {b.personal_reflection}
          </p>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <PatternStat label="entries" value={patterns.entry_count} />
            <PatternStat label="days journaled" value={patterns.days_journaled} />
            {patterns.overall?.mood != null && <PatternStat label="avg mood" value={`${patterns.overall.mood}/10`} />}
            {patterns.overall?.energy != null && <PatternStat label="avg energy" value={`${patterns.overall.energy}/10`} />}
          </div>
          <p className="mt-5 font-serif-italic text-[color:var(--text-muted)] text-xs">
            Averages computed from your journal entries. Log more entries across varied field states to sharpen the correlations.
          </p>
        </div>
      )}

      {Array.isArray(b.wellbeing) && b.wellbeing.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center gap-2 label-overline text-[color:var(--gold)]">
            <Compass size={12} /> Grounding suggestions
          </div>
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {b.wellbeing.map((w, i) => (
              <li
                key={i}
                className="rounded-xl border border-[color:var(--gold)]/15 hover:border-[color:var(--gold)]/40 p-5 hover-lift"
                data-testid={`wellbeing-item-${i}`}
                style={{ background: "rgba(28, 38, 74, 0.4)" }}
              >
                <span className="font-serif-italic text-[color:var(--gold)] text-lg mr-2">
                  0{i + 1}
                </span>
                <span className="text-[color:var(--parchment-soft)]">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(Array.isArray(b.physical_effects) && b.physical_effects.length > 0) ||
      (Array.isArray(b.emotional_effects) && b.emotional_effects.length > 0) ? (
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="effects-grid">
          {Array.isArray(b.physical_effects) && b.physical_effects.length > 0 && (
            <div
              className="rounded-2xl border border-[color:var(--rose-gold)]/20 p-7"
              style={{ background: "rgba(28, 38, 74, 0.4)" }}
              data-testid="physical-effects-card"
            >
              <div className="flex items-center gap-2 label-overline text-[color:var(--rose-gold)]">
                <Activity size={12} /> Possible physical patterns
              </div>
              <ul className="mt-5 space-y-3">
                {b.physical_effects.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[color:var(--parchment-soft)] font-sans-body leading-relaxed"
                    data-testid={`physical-effect-${i}`}
                  >
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--rose-gold)] shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(b.emotional_effects) && b.emotional_effects.length > 0 && (
            <div
              className="rounded-2xl border border-[color:var(--gold)]/20 p-7"
              style={{ background: "rgba(28, 38, 74, 0.4)" }}
              data-testid="emotional-effects-card"
            >
              <div className="flex items-center gap-2 label-overline text-[color:var(--gold)]">
                <Heart size={12} /> Possible emotional patterns
              </div>
              <ul className="mt-5 space-y-3">
                {b.emotional_effects.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[color:var(--parchment-soft)] font-sans-body leading-relaxed"
                    data-testid={`emotional-effect-${i}`}
                  >
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--gold)] shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}

      {Array.isArray(b.manifestation_opportunities) && b.manifestation_opportunities.length > 0 && (
        <div className="mt-10" data-testid="manifestation-section">
          <div className="flex items-center gap-2 label-overline text-[color:var(--parchment)]">
            <Sparkle size={12} /> Manifestation opportunities
          </div>
          <p className="mt-2 font-serif-italic text-[color:var(--text-secondary)] text-sm">
            What today&apos;s field is well-suited to hold.
          </p>
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {b.manifestation_opportunities.map((m, i) => (
              <li
                key={i}
                className="rounded-xl border border-[color:var(--parchment)]/15 hover:border-[color:var(--parchment)]/40 p-5 hover-lift flex items-start gap-3"
                style={{ background: "rgba(245, 230, 200, 0.03)" }}
                data-testid={`manifestation-item-${i}`}
              >
                <span className="font-serif-italic text-[color:var(--parchment)] text-2xl leading-none mt-0.5">
                  ✧
                </span>
                <span className="text-[color:var(--parchment-soft)] font-sans-body leading-relaxed">
                  {m}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-4 font-serif-italic text-[color:var(--text-muted)] text-xs">
            Reported patterns for reflection — not medical advice or prediction.
          </p>
        </div>
      )}

      {Array.isArray(b.tags) && (
        <div className="mt-8 flex flex-wrap gap-2">
          {b.tags.map((t) => (
            <span
              key={t}
              className="px-3 py-1 rounded-full border border-[color:var(--gold)]/20 text-[color:var(--text-secondary)] label-overline"
            >
              #{t}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
